import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createFormData, createURLPath, sanitizeFileName } from "@/lib/utils";
import { APIResponseSuccessSchema } from "@/schema/api";
import { UploadSchema } from "@/schema/uploads";
import { z } from "zod";

export const UploadApiFactory = {
  Endpoint: mBaseURL + "/api/upload",
  Schemas: {
    Response: {
      UploadFile: APIResponseSuccessSchema.extend({
        data: UploadSchema,
      }),
      UploadURL: APIResponseSuccessSchema.extend({
        data: UploadSchema,
      }),
      DeleteFile: APIResponseSuccessSchema.extend({
        data: z.object({
          success: z.boolean(),
          message: z.string(),
          file_key: z.string(),
        }),
      }),
    },
  },
  Apis: {
    ProxyFile: async (url: string) => {
      return api(createURLPath(UploadApiFactory.Endpoint, "proxy?url=" + url), {
        method: "GET",
      }).then((response) => response.blob());
    },
    UploadFile: async (file: File) => {
      const form = createFormData({
        file: [file, sanitizeFileName(file.name)],
      });

      const response = await api(createURLPath(UploadApiFactory.Endpoint, "file"), {
        method: "POST",
        multipart: true,
        body: form,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(UploadApiFactory.Schemas.Response.UploadFile.parse);
    },
    UploadURL: async (url: string) => {
      const response = await api(createURLPath(UploadApiFactory.Endpoint, "url"), {
        method: "POST",
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(UploadApiFactory.Schemas.Response.UploadURL.parse);
    },
    DeleteFile: async (file_id: string) => {
      const response = await api(createURLPath(UploadApiFactory.Endpoint, "files", file_id), {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(UploadApiFactory.Schemas.Response.DeleteFile.parse);
    },
    UploadPresignedURL: async ({ file, url }: { file: File; url: string }) => {
      const form = createFormData({
        file: [file, file.name],
        url: [url],
      });

      const response = await api(createURLPath(UploadApiFactory.Endpoint, "presigned"), {
        method: "POST",
        multipart: true,
        body: form,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(APIResponseSuccessSchema.parse);
    },
  },
};
